/**
 * Copilot store — conversation, proposed actions, execution timeline and
 * widget chrome state (position / minimized / mode are persisted).
 *
 * Degraded mode: the copilot backend (/api/v1/copilot/*) IS deployed, so a
 * failure is only "not deployed" when the endpoint is genuinely absent
 * (404 / 501 — an old or rolled-back backend). Then `backendAvailable` flips to
 * false, the history stays local, and lightweight simulated actions are proposed
 * with the `preview` flag ("aperçu" badge). A transient failure (429 / 5xx /
 * network) must NOT masquerade as "not deployed / simulated" — that is an
 * invisible failure; it surfaces an honest error and leaves the backend marked
 * available.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { api, ApiError } from '@/lib/api/client';
import {
  isWaitUserConfirm,
  paceDelayMs,
  runPacedStep,
  runRegisteredAction,
} from '@/features/copilot/action-executor';
import type { CopilotPace, PacedStepOutcome } from '@/features/copilot/action-executor';
import { normalizeActions } from '@/features/copilot/normalize';
import type { Block } from '@/features/copilot/rich/blocks';
import type {
  CopilotAction,
  CopilotMessage,
  CopilotMode,
  CopilotTimelineEntry,
  CopilotToast,
  ExecutionStatus,
  PageContext,
} from '@/features/copilot/types';

const FALLBACK_ASSISTANT_CONTENT =
  'Backend copilot non déployé — vos messages sont conservés localement.';

function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function defaultPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return { x: 24, y: 24 };
  return {
    x: Math.max(16, window.innerWidth - 396),
    y: Math.max(16, window.innerHeight - 616),
  };
}

/**
 * Local heuristic used when the backend is unavailable: propose simulated
 * ("aperçu") actions so the Action Protocol can still be demonstrated.
 */
function buildPreviewActions(content: string, pageContext: PageContext | null): CopilotAction[] {
  const actions: CopilotAction[] = [];

  const routeMatch = content.match(/(?:^|\s)(\/[a-z0-9][a-z0-9\-_/]*)/i);
  if (routeMatch) {
    actions.push({
      action_id: uid(),
      type: 'navigate',
      target: { route: routeMatch[1] },
      requires_confirmation: true,
      preview: true,
    });
  }

  if (pageContext?.formSchema && /rempli|formulaire|champ|fill/i.test(content)) {
    actions.push({
      action_id: uid(),
      type: 'highlight',
      target: { page_id: pageContext.pageId, form_id: pageContext.formSchema.formId },
      requires_confirmation: true,
      preview: true,
    });
  }

  return actions;
}

interface SessionResponse {
  id: string;
}

interface MessageResponse {
  // `view` is the optional Copilot View Protocol payload the backend attaches
  // to the assistant message (ai-orchestrator `_message_to_dict`). It is
  // untyped on the wire; `sanitizeView` coerces it to a Block[] or null.
  message: { role: 'assistant'; content: string; view?: unknown };
  actions?: CopilotAction[];
}

/**
 * Accept `message.view` only when it is a non-empty array of block-shaped
 * objects, else null. The backend validator already drops malformed blocks and
 * the renderer degrades a bad block to nothing, so this is a light structural
 * guard (array of objects), not a full schema check — it keeps a stray scalar
 * or `{}` from ever reaching the renderer as a "view".
 */
function sanitizeView(raw: unknown): Block[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const blocks = raw.filter(
    (b): b is Block => typeof b === 'object' && b !== null && typeof (b as { type?: unknown }).type === 'string',
  );
  return blocks.length > 0 ? blocks : null;
}

// ---------------------------------------------------------------------------
// Paced player — opt-in "watch & intervene" mode for the agent-mode auto-run
// queue (see `sendMessage` below). Default behaviour (`playerRunMode: 'auto'`)
// is untouched — the original fire-and-forget sequential loop still runs
// byte-for-byte as before. Switching to 'paced' routes the same non-confirm
// actions, PLUS `wait_user_confirm` (otherwise always confirmation-gated and
// silently skipped — see action-executor.ts), through this queue so the user
// can watch every step render, pause, step, stop, or intervene mid-run.
// Confirm-gated actions (submit_form / import_records / execute_tool) are
// UNCHANGED in both modes: they still land in `pendingActions` and only run
// via the existing Confirmer/Rejeter cards + `/actions/{id}/confirm` CASL
// gate — the player only paces the sequence AROUND them.
// ---------------------------------------------------------------------------

export type PlayerRunMode = 'auto' | 'paced';
export type PlayerStatus = 'idle' | 'playing' | 'paused' | 'waiting' | 'done';
export type PlayerPace = CopilotPace;
export type PlayerStepStatus = 'pending' | 'running' | 'waiting' | ExecutionStatus;

export interface PlayerStep {
  action: CopilotAction;
  status: PlayerStepStatus;
  detail?: string;
}

/** A sleep that returns early once `isCancelled` flips true — keeps Pause/Stop responsive. */
async function sleepInterruptible(ms: number, isCancelled: () => boolean): Promise<void> {
  const tick = 100;
  let elapsed = 0;
  while (elapsed < ms) {
    if (isCancelled()) return;
    const wait = Math.min(tick, ms - elapsed);
    await new Promise((resolve) => setTimeout(resolve, wait));
    elapsed += wait;
  }
}

// Monotonically-increasing token: playerStop()/playerPlay()/playerContinueWait()
// each bump it so a stale in-flight loop (mid-await on a step) never resumes or
// overwrites state after being superseded by a newer one.
let playerRunToken = 0;

/**
 * Drives the paced player queue one action at a time via `runPacedStep`
 * (shared with the executor so a paced action always lands in the same
 * global timeline as an auto-run/confirmed one). Waits a pace-dependent delay
 * between steps, unless the action is `wait_user_confirm`, in which case the
 * run PAUSES (`status: 'waiting'`) until the user answers via
 * `playerContinueWait` (Continuer) or `playerStop` (Arrêter).
 */
async function runPlayerLoop(opts?: { singleStep?: boolean }): Promise<void> {
  const myToken = ++playerRunToken;
  for (;;) {
    const state = useCopilotStore.getState();
    if (playerRunToken !== myToken || state.playerStatus !== 'playing') return;
    const { playerSteps, playerStepIndex } = state;
    if (playerStepIndex >= playerSteps.length) {
      useCopilotStore.setState({ playerStatus: 'done' });
      return;
    }
    const step = playerSteps[playerStepIndex];

    if (!isWaitUserConfirm(step.action)) {
      useCopilotStore.setState((s) => {
        const steps = s.playerSteps.slice();
        if (steps[playerStepIndex]) steps[playerStepIndex] = { ...steps[playerStepIndex], status: 'running' };
        return { playerSteps: steps };
      });
    }

    const outcome: PacedStepOutcome = await runPacedStep(step.action);

    if (playerRunToken !== myToken) return; // superseded while awaiting

    if (outcome.kind === 'waiting') {
      useCopilotStore.setState((s) => {
        const steps = s.playerSteps.slice();
        if (steps[playerStepIndex]) steps[playerStepIndex] = { ...steps[playerStepIndex], status: 'waiting' };
        return { playerSteps: steps, playerStatus: 'waiting', playerWaitPrompt: outcome.prompt };
      });
      return;
    }

    const { result } = outcome;
    useCopilotStore.setState((s) => {
      const steps = s.playerSteps.slice();
      if (steps[playerStepIndex]) {
        steps[playerStepIndex] = { ...steps[playerStepIndex], status: result.status, detail: result.detail };
      }
      return { playerSteps: steps, playerStepIndex: playerStepIndex + 1 };
    });

    if (opts?.singleStep) {
      useCopilotStore.setState((s) => ({
        playerStatus: s.playerStepIndex >= s.playerSteps.length ? 'done' : 'paused',
      }));
      return;
    }

    const delay = paceDelayMs(useCopilotStore.getState().playerPace) + (step.action.type === 'navigate' ? 400 : 0);
    await sleepInterruptible(
      delay,
      () => playerRunToken !== myToken || useCopilotStore.getState().playerStatus !== 'playing',
    );
  }
}

interface CopilotState {
  // Widget chrome (position / minimized / mode are persisted)
  open: boolean;
  minimized: boolean;
  position: { x: number; y: number };
  mode: CopilotMode;

  // Advanced power-user options (persisted). Sent to the backend as `options`;
  // ignored gracefully until the copilot service consumes them.
  model: string;
  temperature: number;
  maxSteps: number;

  // Conversation
  sessionId: string | null;
  messages: CopilotMessage[];
  pendingActions: CopilotAction[];
  timeline: CopilotTimelineEntry[];
  toasts: CopilotToast[];
  backendAvailable: boolean;
  sending: boolean;

  // Paced player — opt-in step-by-step execution of the agent-mode auto-run
  // queue. `playerRunMode`/`playerPace` are persisted preferences; the run
  // itself is session-local (like `messages`/`sessionId`).
  playerRunMode: PlayerRunMode;
  playerPace: PlayerPace;
  playerStatus: PlayerStatus;
  playerSteps: PlayerStep[];
  playerStepIndex: number;
  playerWaitPrompt: string | null;

  // Actions
  toggleOpen: () => void;
  openWidget: () => void;
  setMinimized: (minimized: boolean) => void;
  setPosition: (position: { x: number; y: number }) => void;
  setMode: (mode: CopilotMode) => void;
  setModel: (model: string) => void;
  setTemperature: (temperature: number) => void;
  setMaxSteps: (maxSteps: number) => void;
  sendMessage: (content: string, pageContext?: PageContext | null) => Promise<void>;
  confirmAction: (actionId: string) => void;
  rejectAction: (actionId: string) => void;
  recordExecution: (action: CopilotAction, status: ExecutionStatus, detail?: string) => void;
  pushToast: (message: string) => void;
  dismissToast: (toastId: string) => void;

  // Paced player controls
  setPlayerRunMode: (runMode: PlayerRunMode) => void;
  setPlayerPace: (pace: PlayerPace) => void;
  playerPlay: () => void;
  playerPause: () => void;
  playerStepOnce: () => void;
  playerStop: () => void;
  playerContinueWait: () => void;
  playerIntervene: (content: string, pageContext?: PageContext | null) => Promise<void>;
}

export const useCopilotStore = create<CopilotState>()(
  persist(
    (set, get) => ({
      open: false,
      minimized: false,
      position: defaultPosition(),
      mode: 'assistant',

      model: 'auto',
      temperature: 0.7,
      maxSteps: 5,

      sessionId: null,
      messages: [],
      pendingActions: [],
      timeline: [],
      toasts: [],
      backendAvailable: true,
      sending: false,

      playerRunMode: 'auto',
      playerPace: 'normal',
      playerStatus: 'idle',
      playerSteps: [],
      playerStepIndex: 0,
      playerWaitPrompt: null,

      toggleOpen: () => set((s) => ({ open: !s.open })),

      openWidget: () => set({ open: true, minimized: false }),

      setMinimized: (minimized) => set({ minimized }),

      setPosition: (position) => set({ position }),

      setMode: (mode) => set({ mode }),

      setModel: (model) => set({ model }),

      setTemperature: (temperature) =>
        set({ temperature: Math.min(1, Math.max(0, temperature)) }),

      setMaxSteps: (maxSteps) => set({ maxSteps: Math.min(10, Math.max(1, Math.round(maxSteps))) }),

      sendMessage: async (content, pageContext) => {
        const text = content.trim();
        if (!text || get().sending) return;

        const userMessage: CopilotMessage = {
          id: uid(),
          role: 'user',
          content: text,
          at: nowIso(),
        };
        set((s) => ({ messages: [...s.messages, userMessage], sending: true }));

        const { mode } = get();
        try {
          let sessionId = get().sessionId;
          if (!sessionId) {
            const session = await api.post<SessionResponse>('/api/v1/copilot/sessions', { mode });
            sessionId = session.id;
            set({ sessionId });
          }

          const { model, temperature, maxSteps } = get();
          const response = await api.post<MessageResponse>(
            `/api/v1/copilot/sessions/${sessionId}/messages`,
            {
              content: text,
              mode,
              page_context: pageContext ?? null,
              // Advanced options — the backend ignores unknown fields until it
              // consumes them (Pydantic default), so this never breaks the call.
              options: { model, temperature, max_steps: maxSteps },
            },
          );

          // Normalize the wire shape (backend sends `id`; the widget keys/confirms
          // actions by `action_id`) so multi-card confirm/reject is always 1:1.
          const actions = normalizeActions(response.actions);

          // `wait_user_confirm` always requires confirmation server-side (see
          // action_protocol.py `_NO_CONFIRM`), so it never joins `autoRun`
          // below — by default it lands as a plain confirm card and, once
          // confirmed, is a silent no-op ('skipped'). The PACED player pulls
          // it into its own step queue instead so it can be honoured as a
          // real pause point (see `runPlayerLoop` above).
          const pacedRun = get().playerRunMode === 'paced' && mode === 'agent';

          // Agent mode auto-executes actions that do not require confirmation;
          // everything else lands in the pending queue (cards in the widget).
          // AUTO (default) mode: identical partition/behaviour to before —
          // untouched. PACED mode: the same non-confirm actions, PLUS
          // `wait_user_confirm`, go to the step-by-step player queue instead
          // of firing back-to-back; every other confirm-gated action still
          // lands in `pendingActions`, unchanged (Confirmer/Rejeter + CASL).
          const autoRun = pacedRun ? [] : actions.filter((a) => !a.requires_confirmation && mode === 'agent');
          const stepQueue = pacedRun
            ? actions.filter((a) => !a.requires_confirmation || isWaitUserConfirm(a))
            : [];
          const pending = pacedRun
            ? actions.filter((a) => a.requires_confirmation && !isWaitUserConfirm(a))
            : actions.filter((a) => a.requires_confirmation || mode !== 'agent');

          const assistantMessage: CopilotMessage = {
            id: uid(),
            role: 'assistant',
            content: response.message.content,
            actions: actions.length > 0 ? actions : undefined,
            // Carry the rich View Protocol payload (tables / KPIs / charts /
            // cards / forms …) so the widget renders it around the text.
            view: sanitizeView(response.message.view),
            at: nowIso(),
          };

          set((s) => ({
            messages: [...s.messages, assistantMessage],
            pendingActions: [...s.pendingActions, ...pending],
            backendAvailable: true,
            sending: false,
          }));

          if (pacedRun) {
            // Queue the steps for the paced player instead of auto-firing
            // them; auto-start playback only when no run is already active
            // so an intervention mid-run just extends the current queue
            // without yanking control back from a paused/waiting user.
            if (stepQueue.length > 0) {
              set((s) => ({
                playerSteps: [
                  ...s.playerSteps,
                  ...stepQueue.map((action) => ({ action, status: 'pending' as const })),
                ],
              }));
              if (get().playerStatus === 'idle' || get().playerStatus === 'done') {
                get().playerPlay();
              }
            }
          } else {
            // BUG D: run auto-executed actions SEQUENTIALLY, not fire-and-forget — a
            // fill_field/submit_form/open_form after a navigate must wait for the
            // destination page to mount (its target elements don't exist yet), and give
            // each navigation a render tick before the next action runs.
            void (async () => {
              for (const action of autoRun) {
                try {
                  await runRegisteredAction(action);
                } catch {
                  /* runRegisteredAction records its own failure outcome */
                }
                if (action.type === 'navigate') {
                  await new Promise((resolve) => setTimeout(resolve, 400));
                }
              }
            })();
          }
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;
          const endpointAbsent = status === 404 || status === 501;
          if (endpointAbsent) {
            // Genuine absence (old / rolled-back backend): degrade to local
            // preview mode with simulated "aperçu" actions.
            const preview = buildPreviewActions(text, pageContext ?? null);
            const assistantMessage: CopilotMessage = {
              id: uid(),
              role: 'assistant',
              content: FALLBACK_ASSISTANT_CONTENT,
              actions: preview.length > 0 ? preview : undefined,
              at: nowIso(),
            };
            set((s) => ({
              messages: [...s.messages, assistantMessage],
              pendingActions: [...s.pendingActions, ...preview],
              backendAvailable: false,
              sending: false,
            }));
            return;
          }
          // The backend IS deployed but this turn failed transiently (rate limit /
          // 5xx / network). Tell the truth — do NOT flip to "simulated" mode or
          // fabricate preview actions, which would hide a real, retryable failure.
          const reason =
            status === 402
              ? 'Crédits IA insuffisants — rechargez votre solde (badge Crédits, en-tête) pour continuer à utiliser le copilot.'
              : status === 429
                ? 'Trop de requêtes — limite temporairement atteinte. Patientez un instant puis réessayez.'
                : status === 0
                  ? 'Copilot momentanément injoignable — vérifiez votre connexion et réessayez.'
                  : 'Le copilot a rencontré une erreur temporaire. Réessayez dans un instant.';
          const assistantMessage: CopilotMessage = {
            id: uid(),
            role: 'assistant',
            content: reason,
            at: nowIso(),
          };
          set((s) => ({
            messages: [...s.messages, assistantMessage],
            // Backend is deployed — keep it marked available; this was transient.
            sending: false,
          }));
        }
      },

      confirmAction: (actionId) => {
        const action = get().pendingActions.find((a) => a.action_id === actionId);
        if (!action) return;
        set((s) => ({
          pendingActions: s.pendingActions.filter((a) => a.action_id !== actionId),
        }));
        void runRegisteredAction(action);
      },

      rejectAction: (actionId) => {
        const action = get().pendingActions.find((a) => a.action_id === actionId);
        if (!action) return;
        set((s) => ({
          pendingActions: s.pendingActions.filter((a) => a.action_id !== actionId),
        }));
        get().recordExecution(action, 'skipped', 'Rejetée par l’utilisateur');
      },

      recordExecution: (action, status, detail) =>
        set((s) => ({
          timeline: [...s.timeline, { action, status, detail, at: nowIso() }],
        })),

      pushToast: (message) => {
        const toast: CopilotToast = { id: uid(), message, at: nowIso() };
        set((s) => ({ toasts: [...s.toasts, toast] }));
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            set((s) => ({ toasts: s.toasts.filter((t) => t.id !== toast.id) }));
          }, 5000);
        }
      },

      dismissToast: (toastId) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== toastId) })),

      // --- Paced player -----------------------------------------------------

      setPlayerRunMode: (runMode) => {
        // Leaving paced mode aborts any in-flight/queued run so nothing keeps
        // dangling in the background once the player UI disappears.
        if (runMode === 'auto') get().playerStop();
        set({ playerRunMode: runMode });
      },

      setPlayerPace: (pace) => set({ playerPace: pace }),

      playerPlay: () => {
        const { playerStatus, playerSteps, playerStepIndex } = get();
        if (playerStatus === 'waiting') return; // resolve the prompt first (Continuer/Arrêter)
        if (playerStepIndex >= playerSteps.length) return; // nothing queued to play
        set({ playerStatus: 'playing' });
        void runPlayerLoop();
      },

      playerPause: () => {
        if (get().playerStatus === 'playing') set({ playerStatus: 'paused' });
      },

      playerStepOnce: () => {
        const { playerStatus, playerSteps, playerStepIndex } = get();
        if (playerStatus === 'playing' || playerStatus === 'waiting') return;
        if (playerStepIndex >= playerSteps.length) return;
        set({ playerStatus: 'playing' });
        void runPlayerLoop({ singleStep: true });
      },

      playerStop: () => {
        const { playerSteps, playerStepIndex, playerStatus } = get();
        const current = playerSteps[playerStepIndex];
        if (current && playerStatus === 'waiting') {
          get().recordExecution(current.action, 'skipped', 'Exécution arrêtée par l’utilisateur');
        }
        playerRunToken += 1; // invalidate any in-flight loop
        set({ playerStatus: 'idle', playerSteps: [], playerStepIndex: 0, playerWaitPrompt: null });
      },

      playerContinueWait: () => {
        const { playerSteps, playerStepIndex, playerStatus } = get();
        if (playerStatus !== 'waiting') return;
        const current = playerSteps[playerStepIndex];
        if (!current || current.action.type !== 'wait_user_confirm') return;
        const steps = playerSteps.slice();
        steps[playerStepIndex] = { ...current, status: 'executed', detail: 'Confirmation utilisateur : poursuite' };
        get().recordExecution(current.action, 'executed', 'Confirmation utilisateur : poursuite');
        set({
          playerSteps: steps,
          playerStepIndex: playerStepIndex + 1,
          playerStatus: 'playing',
          playerWaitPrompt: null,
        });
        void runPlayerLoop();
      },

      playerIntervene: async (content, pageContext) => {
        const text = content.trim();
        if (!text) return;
        // Pause an active run before sending so the intervention and the
        // auto-advancing queue never race on screen; a run already waiting on
        // `wait_user_confirm` stays 'waiting' (still resolved via Continuer).
        set((s) => ({ playerStatus: s.playerStatus === 'waiting' ? 'waiting' : 'paused' }));
        await get().sendMessage(text, pageContext ?? null);
      },
    }),
    {
      name: 'psn-copilot',
      version: 1,
      partialize: (s) => ({
        position: s.position,
        minimized: s.minimized,
        mode: s.mode,
        model: s.model,
        temperature: s.temperature,
        maxSteps: s.maxSteps,
        playerRunMode: s.playerRunMode,
        playerPace: s.playerPace,
      }),
    },
  ),
);
